import { Card, Center, HStack, Flex, useToast, Text, Spacer, IconButton, Icon } from "@chakra-ui/react";
import { usePrestudyContext } from "../context/Prestudy/PrestudyContextProvider";
import { CHILD_WIDTH_PERCENTAGE, SPACING } from "../constants/Constants";
import { useEffect, useRef, useState } from "react";
import Input from "../components/Input";
import { English } from "../Texts";
import Button from "../components/Button";
import { durationBetweenTimes } from "../utility-functions/TimeUtils";
import ActivityCard from "../components/prestudy-splitter/ActivityCard";
import ParticipantCard from "../components/prestudy-splitter/ParticipantCard";
import DoubleDivider from "../components/DoubleDivider";
import { PerParticipantTokensAndActivity, calculatePrestudyDistrubiton, stringSummary } from "../utility-functions/PrestudyUtils";
import ParticipantTimeCard from "../components/prestudy-splitter/ParticipantTimeCard";
import { Activity } from "../context/Prestudy/Activity";
import { FaAccusoft, FaCopy } from "react-icons/fa";

export default function PrestudySplitter() {
    const [perParticipantTokens, setPerParticipantTokens] = useState<PerParticipantTokensAndActivity[]>([]);
    const [totalDurationInMinutes, setTotalDurationInMinutes] = useState<number>(0);
    const [totalDurationInHalfHours, setTotalDurationInHalfHours] = useState<number>(0);
    const [difference, setDifference] = useState<number>(0);
    const [activitiesWithDurations, setActivitiesWithDurations] = useState<Activity[]>([]);

    const toast = useToast();
    const context = usePrestudyContext();
    const texts = English.prestudySplitter;

    const refActivityNumber = useRef<HTMLInputElement>(null);
    const refTimeFrom = useRef<HTMLInputElement>(null);
    const refTimeTo = useRef<HTMLInputElement>(null);
    const refParticipantName = useRef<HTMLInputElement>(null);


    const onAddActivity = () => {
        if (refActivityNumber.current === null || refTimeFrom.current === null || refTimeTo.current === null) return; // Should never happen
        const activityNumber = Number(refActivityNumber.current.value);
        if (context.activityContext.activities.map(activity => activity.number).includes(activityNumber)) {
            toast({
                ...texts.errors.existingActivity,
                status: "warning",
                isClosable: true,
            });
            return;
        }

        const timeFrom = refTimeFrom.current.value;
        const timeTo = refTimeTo.current.value;
        const duration = durationBetweenTimes(timeFrom, timeTo);
        if (duration === undefined || activityNumber <= 1000) {
            const description = activityNumber <= 1000 ? texts.errors.invalidActivityNumber : texts.errors.invalidDuration;
            toast({
                ...description,
                status: "warning",
                isClosable: true,
            });
            return;
        }
        context.activityContext.addActivity({ number: activityNumber, duration: duration });
        refActivityNumber.current.value = "";
        refTimeFrom.current.value = refTimeTo.current.value;
        refTimeTo.current.value = "";
    };

    const onAddParticipant = (e?: React.FormEvent) => {
        e?.preventDefault();
        if (refParticipantName.current === null) return; // Should never happen
        if (refParticipantName.current.value.trim() === "") {
            toast({
                ...texts.errors.invalidParticipantName,
                status: "warning",
                isClosable: true,
            });
            return;
        }
        if (context.participantContext.participants.map(name => name.toLowerCase().trim()).includes(refParticipantName.current.value.toLowerCase().trim())) {
            toast({
                ...texts.errors.existingParticipant,
                status: "warning",
                isClosable: true,
            });
            return;
        }
        context.participantContext.addParticipant(refParticipantName.current.value);
        refParticipantName.current.value = "";
    }

    useEffect(() => {
        const result = calculatePrestudyDistrubiton(context.activityContext.activities, context.participantContext.participants);
        if (result.difference !== 0) {
            toast({
                title: texts.errors.differingTokens.title,
                description: texts.errors.differingTokens.description.replace("%difference%", result.difference.toString()),
                status: "error",
                isClosable: true,
            });
        }
        setPerParticipantTokens(result.perParticipantTokensAndActivity);
        setTotalDurationInMinutes(result.totalDurationInMinutes);
        setTotalDurationInHalfHours(result.totalDurationInHalfHours);
        setDifference(result.difference);
        setActivitiesWithDurations(result.totalPerActivity);
    }, [texts, toast, context.activityContext.activities, context.participantContext.participants]);

    return (
        <Center>
            <Card w={CHILD_WIDTH_PERCENTAGE} m={SPACING}>
                <Center>
                    <form onSubmit={onAddParticipant}>
                        <Flex align="end">
                            <Input ref={refParticipantName} label={texts.labels.participantName} />
                            <Button onClick={onAddParticipant}> {texts.labels.addParticipant} </Button>
                        </Flex>
                    </form>
                </Center>
                <Center>
                    <HStack m={SPACING} my={SPACING / 2}>
                        {
                            context.participantContext.participants.map((participant) => (
                                <ParticipantCard key={participant} participantName={participant} />
                            ))
                        }
                        {context.participantContext.participants.length === 0 && <Text>{texts.labels.participantListEmpty}</Text>}
                    </HStack>
                </Center>
                <DoubleDivider />
                <Center>
                    <Flex align="end">
                        <Input ref={refActivityNumber} label={texts.labels.activityNumber} />
                        <Input ref={refTimeFrom} type="time" label={texts.labels.startTime} />
                        <Input ref={refTimeTo} type="time" label={texts.labels.endTime} />
                        <Button onClick={onAddActivity}> {texts.labels.addActivity} </Button>
                    </Flex>
                </Center>
                <Center>
                    <HStack m={SPACING} my={SPACING / 2}>
                        {
                            context.activityContext.activities.map((activity) => (
                                <ActivityCard key={activity.number} activity={activity} />
                            ))
                        }
                        {context.activityContext.activities.length === 0 && <Text h={SPACING}>{texts.labels.activityListEmpty}</Text>}
                    </HStack>
                </Center>
                <DoubleDivider />
                <Center>
                    <Flex>
                        {activitiesWithDurations.map((activity) => (
                            <Card key={activity.number} variant={"elevated"} bg={"gray"} px={SPACING / 4} m={SPACING / 2} textAlign={"center"}>
                                <Text mx={SPACING / 4}>A: {activity.number} </Text>
                                <Flex>
                                    <Text>{activity.duration}</Text>
                                    <Spacer />
                                    <Text>{"-"}</Text>
                                    <Spacer />
                                    <Text>{activity.reportedDuration} </Text>
                                </Flex>
                            </Card>
                        ))}
                    </Flex>
                </Center>
                <Center my={SPACING / 4}>
                    <Card variant={"outline"} px={SPACING / 2} mx={SPACING / 4}>
                        <Text> {texts.labels.sessionDuration}: {totalDurationInMinutes} (~{totalDurationInHalfHours})</Text>
                    </Card>
                    <Card variant={"outline"} px={SPACING / 2} mx={SPACING / 4}>
                        <Text> {texts.labels.participants}: {context.participantContext.participants.length}</Text>
                    </Card>
                    <Card variant={"outline"} px={SPACING / 2} mx={SPACING / 4}>
                        <Text> {texts.labels.activities}: {context.activityContext.activities.length}</Text>
                    </Card>
                    {difference !== 0 && <Card variant={"outline"} px={SPACING / 2} mx={SPACING / 4} bg="crimson">
                        <Text> {texts.errors.differingTokens.shortDescription.replace("%minutes%", (difference * 30).toString())}</Text>
                    </Card>}
                </Center>
                <Center mb={SPACING / 2}>
                    <HStack>
                        <ParticipantTimeCard perParticipantTokens={perParticipantTokens} />
                    </HStack>
                </Center>
                <IconButton aria-label="Copy" icon={<Icon as={FaCopy} />} onClick={() => navigator.clipboard.writeText(stringSummary(perParticipantTokens, activitiesWithDurations))} />
            </Card>
        </Center>
    )
};
